import { useEffect, useMemo, useState, type ReactNode } from 'react';
import api from '../lib/apiClient';

export interface CatalogRole {
  name: string;
  label: string;
  category: 'system' | 'module' | 'self_service';
  description: string;
}

const CATEGORY_LABELS: Record<CatalogRole['category'], string> = {
  system: 'System',
  module: 'Modules',
  self_service: 'Self-service',
};
const CATEGORY_ORDER: CatalogRole['category'][] = ['system', 'module', 'self_service'];

// Narrow, individually-scoped roles shown on the "Roles" tab instead of
// "Permissions" — self-service identities (Member/Supplier/Customer/
// Employer) and one-off approval-chain/sign-off steps (the Benefits chain).
// Kept as an explicit allowlist rather than reusing `category` directly so
// an individual role can be moved between tabs without that carrying any
// RBAC meaning.
const NARROW_ROLE_NAMES = new Set([
  'member',
  'supplier',
  'customer',
  'employer',
  'penalties_senior_approver',
  'memberships_benefits_level_reviewer',
  'memberships_benefits_supervisor',
  'memberships_benefits_manager',
  'memberships_benefits_senior_approver',
  'memberships_benefits_cheque_signatory',
]);

interface RolePickerProps {
  value: string[];
  onChange: (roles: string[]) => void;
  // 'permissions': broad module/system roles (the "Permissions" tab).
  // 'roles': the narrow roles in NARROW_ROLE_NAMES (the "Roles" tab).
  variant: 'permissions' | 'roles';
  // A control (e.g. the Supplier org picker) to render inline next to a
  // specific role's checkbox — keyed by role name.
  inlineControls?: Partial<Record<string, ReactNode>>;
}

// Grouped, searchable multi-select for role assignment — see GET /roles
// (RoleController -> App\Support\RoleCatalog) for the label/category/
// description each role renders with. The "Roles" tab's list is short
// enough to render unboxed, flat, and without category headings (it's a
// single category today) rather than in the "Permissions" tab's bordered,
// scrollable, headinged box.
export default function RolePicker({ value, onChange, variant, inlineControls }: RolePickerProps) {
  const boxed = variant === 'permissions';
  const [roles, setRoles] = useState<CatalogRole[] | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get<CatalogRole[]>('/roles').then((res) => setRoles(res.data));
  }, []);

  const groups = useMemo(() => {
    if (!roles) return [];
    const inVariant = roles.filter((r) => NARROW_ROLE_NAMES.has(r.name) === (variant === 'roles'));
    const q = search.trim().toLowerCase();
    const filtered = q
      ? inVariant.filter((r) => r.label.toLowerCase().includes(q) || r.description.toLowerCase().includes(q))
      : inVariant;
    return CATEGORY_ORDER.map((category) => ({
      category,
      items: filtered.filter((r) => r.category === category),
    })).filter((g) => g.items.length > 0);
  }, [roles, search, variant]);

  const isSystemAdmin = value.includes('system_admin');

  const toggle = (name: string) => {
    if (name === 'system_admin') {
      // System Administrator bypasses every permission check on its own —
      // holding other roles alongside it is inert, so selecting it clears
      // (and locks out) every other role rather than leaving stale, useless
      // selections behind.
      onChange(isSystemAdmin ? [] : ['system_admin']);
      return;
    }
    onChange(value.includes(name) ? value.filter((r) => r !== name) : [...value, name]);
  };

  if (!roles) return <p className="muted">Loading roles…</p>;

  return (
    <div className={boxed ? 'role-picker' : 'role-picker role-picker-unboxed'}>
      <input
        type="search"
        placeholder="Search roles…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="role-picker-search"
      />
      <div className={boxed ? 'role-picker-groups' : 'role-picker-groups role-picker-groups-flat'}>
        {groups.map((g) => (
          <div key={g.category} className="role-picker-group">
            {boxed && <div className="field-heading">{CATEGORY_LABELS[g.category]}</div>}
            {g.items.map((r) => {
              const disabled = isSystemAdmin && r.name !== 'system_admin';
              const inlineControl = inlineControls?.[r.name];
              return (
                <label key={r.name} className={`role-picker-item${disabled ? ' disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={value.includes(r.name)}
                    disabled={disabled}
                    onChange={() => toggle(r.name)}
                  />
                  <span>
                    <span className="role-picker-item-heading">
                      <strong>{r.label}</strong>
                      {inlineControl}
                    </span>
                    <small className="muted">{r.description}</small>
                  </span>
                </label>
              );
            })}
          </div>
        ))}
        {groups.length === 0 && <p className="muted">No roles match "{search}".</p>}
      </div>
    </div>
  );
}
